// The browser half of crypto/ContentCipher.kt (docs/web-konzept.md, section 5.5). Same wire format
// as the app and the server: 12 bytes random IV, ciphertext, 16 bytes tag, AES-256-GCM, with the
// kind's tag as associated data. A blob is always decrypted completely.

/** What a blob encrypted with a message content key contains; mirrors ContentKind in crypto/ContentCipher.kt. */
export const ContentKind = {
  AUDIO: "voxie/audio/v1",
  WAVEFORM: "voxie/waveform/v1",
  TRANSCRIPT: "voxie/transcript/v1",
  SEGMENT: "voxie/segment/v1",
  MOOD: "voxie/mood/v1",
  IMAGE: "voxie/image/v1",
  THUMB: "voxie/thumb/v1",
  FILE_NAME: "voxie/filename/v1",
  MEDIA: "voxie/media/v1",
  TEXT: "voxie/text/v1",
  TOPIC: "voxie/topic/v1",
  AVATAR: "voxie/avatar/v1",
  LINK_PREVIEW: "voxie/link-preview/v1",
  DOCUMENT: "voxie/document/v1",
  MINIAPP: "voxie/miniapp/v1",
  LOCATION: "voxie/location/v1",
  GIF: "voxie/gif/v1",
  AGENT_TASK: "voxie/agent-task/v1",
  GESTALT: "voxie/gestalt/v1",
  BOARD: "voxie/board/v1",
} as const;

export type ContentKind = (typeof ContentKind)[keyof typeof ContentKind];

export const KEY_BYTES = 32;
const IV_BYTES = 12;
const TAG_BYTES = 16;

/** How much longer a blob is than its plaintext. */
export const OVERHEAD_BYTES = IV_BYTES + TAG_BYTES;

const ascii = new TextEncoder();

/** The kind's tag as bytes, the associated data of every blob of that kind. */
export function associatedData(kind: ContentKind): Uint8Array<ArrayBuffer> {
  return ascii.encode(kind);
}

export function generateKey(): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(KEY_BYTES));
}

async function aesKey(key: Uint8Array<ArrayBuffer>, use: "encrypt" | "decrypt"): Promise<CryptoKey> {
  if (key.length !== KEY_BYTES) throw new Error(`AES-256-GCM needs a 256 bit key, got ${key.length * 8} bit`);
  return crypto.subtle.importKey("raw", key, "AES-GCM", false, [use]);
}

export async function encrypt(kind: ContentKind, plaintext: Uint8Array<ArrayBuffer>, key: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const sealed = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: associatedData(kind), tagLength: TAG_BYTES * 8 },
    await aesKey(key, "encrypt"),
    plaintext,
  );
  const blob = new Uint8Array(IV_BYTES + sealed.byteLength);
  blob.set(iv);
  blob.set(new Uint8Array(sealed), IV_BYTES);
  return blob;
}

/** Throws if the key is wrong or the blob is not what was encrypted (as kind). */
export async function decrypt(kind: ContentKind, blob: Uint8Array<ArrayBuffer>, key: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  if (blob.length < OVERHEAD_BYTES) throw new Error("blob is shorter than IV and tag");
  const opened = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: blob.subarray(0, IV_BYTES), additionalData: associatedData(kind), tagLength: TAG_BYTES * 8 },
    await aesKey(key, "decrypt"),
    blob.subarray(IV_BYTES),
  );
  return new Uint8Array(opened);
}
